import React, { useState } from 'react';
import { exportPackage } from '../../utils/financialAnalystApi';
import './FinancialAnalyst.css';

const EXPORT_FORMATS = [
  {
    key: 'pdf',
    name: 'PDF Report',
    desc: 'AI-formatted professional report with executive summary, key findings, and detailed analysis',
    icon: 'PDF',
    iconClass: 'pdf',
  },
  {
    key: 'xlsx',
    name: 'Excel Workbook',
    desc: 'Multi-sheet workbook with summary, critique lines, and narratives',
    icon: 'XLS',
    iconClass: 'xlsx',
  },
  {
    key: 'docx',
    name: 'Word Document',
    desc: 'Narrative report formatted for Word / Google Docs',
    icon: 'DOC',
    iconClass: 'docx',
  },
];

const ExportPanel = ({ packageId, onClose }) => {
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [options, setOptions] = useState({
    include_gl_detail: true,
    include_charts: true,
    branded: true,
    force: false,
  });
  const [exporting, setExporting] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [error, setError] = useState(null);

  const handleExport = async (forceRegenerate = false) => {
    if (!selectedFormat) return;
    setExporting(true);
    setError(null);
    setDownloaded(false);

    try {
      await exportPackage(packageId, {
        format: selectedFormat,
        options: { ...options, force: forceRegenerate },
      });
      setDownloaded(true);
    } catch (err) {
      console.error('Export failed:', err);
      setError('Export failed. Please try again.');
    }
    setExporting(false);
  };

  return (
    <div className="fa-export-panel">
      <div className="fa-export-panel-header">
        <h3>Export Package</h3>
        <button className="fa-btn fa-btn-sm fa-btn-ghost" onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div className="fa-export-panel-body">
        <div className="fa-form-label" style={{ marginBottom: 12 }}>Choose Format</div>

        {EXPORT_FORMATS.map((fmt) => (
          <div
            key={fmt.key}
            className={`fa-export-option ${selectedFormat === fmt.key ? 'selected' : ''}`}
            onClick={() => { setSelectedFormat(fmt.key); setDownloaded(false); }}
          >
            <div className={`fa-export-icon ${fmt.iconClass}`}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>{fmt.icon}</span>
            </div>
            <div>
              <div className="fa-export-option-title">{fmt.name}</div>
              <div className="fa-export-option-desc">{fmt.desc}</div>
            </div>
          </div>
        ))}

        {/* Options */}
        <div style={{ marginTop: 20 }}>
          <div className="fa-form-label" style={{ marginBottom: 10 }}>Options</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={options.include_gl_detail}
              onChange={(e) => setOptions({ ...options, include_gl_detail: e.target.checked })}
            />
            Include GL transaction detail
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={options.include_charts}
              onChange={(e) => setOptions({ ...options, include_charts: e.target.checked })}
            />
            Include charts and visualizations
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={options.branded}
              onChange={(e) => setOptions({ ...options, branded: e.target.checked })}
            />
            Apply brand styling
          </label>
        </div>

        {error && <div style={{ color: '#EF4444', fontSize: 13, marginTop: 12 }}>{error}</div>}

        {downloaded && (
          <div style={{
            marginTop: 16,
            padding: 14,
            background: '#F0FDF4',
            border: '1px solid #BBF7D0',
            borderRadius: 8,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#15803D' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ verticalAlign: 'middle', marginRight: 6 }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              Download started!
            </div>
          </div>
        )}
      </div>

      <div className="fa-export-panel-footer">
        <button
          className="fa-btn fa-btn-primary"
          onClick={() => handleExport(false)}
          disabled={!selectedFormat || exporting}
          style={{ flex: 1, justifyContent: 'center' }}
        >
          {exporting ? (
            <>
              <div className="fa-progress-spinner" style={{ width: 14, height: 14, borderWidth: 2, marginBottom: 0 }} />
              Generating {selectedFormat?.toUpperCase()}...
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Export {selectedFormat ? selectedFormat.toUpperCase() : ''}
            </>
          )}
        </button>
        {selectedFormat === 'pdf' && downloaded && (
          <button
            className="fa-btn fa-btn-sm"
            onClick={() => handleExport(true)}
            disabled={exporting}
            style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
            title="Force regenerate the PDF with fresh AI formatting"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Regenerate
          </button>
        )}
      </div>
    </div>
  );
};

export default ExportPanel;
